import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CalendarDay } from '@app/shared/models/calendar-day';
import { ExtendedDay } from '@app/shared/models/extended-day';
import { PortalEvent, SingleDayEvent } from '@app/shared/models/portal-event';
import { DateUtils, moment } from '@app/utils/date';
import { Logger } from '@app/utils/logger';
import { ClndrInstance, Month, RenderData } from 'clndr';

@Component({
  selector: 'portal-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  standalone: false,
})
export class CalendarComponent implements OnChanges, OnInit {
  @ViewChild('el', { read: ElementRef, static: true })
  el: ElementRef;

  @Output()
  onEventSelected: EventEmitter<PortalEvent<any>> = new EventEmitter();

  @Output()
  onCreateEvent: EventEmitter<CalendarDay> = new EventEmitter();

  @Output()
  onEditEvent: EventEmitter<PortalEvent<any>> = new EventEmitter();

  @Output()
  onRemoveEvent: EventEmitter<PortalEvent<any>> = new EventEmitter();

  @Input()
  columns: number = 1;

  @Input()
  dayTemplate: TemplateRef<any>;

  @Input()
  events: PortalEvent<any>[] = [];

  @Input()
  months: number = 1;

  @Input()
  startDate?: string = undefined;

  calendar: RenderData;
  instance: ClndrInstance;

  private firstDayOfYear = moment().month(0).dayOfYear(1);

  get heading() {
    let heading = '';

    if (this.calendar) {
      if (this.calendar.months) {
        if (this.calendar.months.length === 12) {
          // Rendering a whole year so we show the year
          heading = this.calendar.months[0].month.get('y').toString();
        } else if (this.calendar.months.length === 1) {
          // Rendering a single month so we show the month
          heading = this.calendar.months[0].month.format('MM.YYYY');
        } else {
          // Rendering a month interval so we show the range
          const from = this.calendar.months[0].month;
          const to = this.calendar.months[this.calendar.months.length - 1].month;

          heading = `${from.format('MM.YYYY')} - ${to.format('MM.YYYY')}`;
        }
      }
    }

    return heading;
  }

  get style() {
    return {
      flexBasis: `${100 / this.columns}%`,
    };
  }

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('events' in changes && changes.events.firstChange === false) {
      this.instance
        .removeEvents(() => true)
        .addEvents(changes.events.currentValue)
        .render();
    } else if ('months' in changes && changes.months.firstChange === false && this.instance !== undefined) {
      const months = changes.months.currentValue;

      if (months === 12) {
        this.instance.setIntervalStart(this.firstDayOfYear);
      }

      this.instance.options.lengthOfTime.months = months;
      this.instance.options.lengthOfTime.interval = months;
      this.instance.render();
    }
  }

  ngOnInit() {
    let startWithMonth: string | moment.Moment | undefined = this.startDate;

    if (startWithMonth == null && this.months === 12) {
      startWithMonth = this.firstDayOfYear;
    }

    this.instance = window['$'](this.el.nativeElement).clndr({
      clickEvents: {
        click: (target) => {
          this.onEventSelected.emit(target.events);
        },
      },
      daysOfTheWeek: ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'],
      events: this.events,
      multiDayEvents: {
        endDate: 'end',
        singleDay: 'date',
        startDate: 'start',
      },
      moment: moment as any,
      lengthOfTime: {
        months: this.months,
        interval: this.months,
      },
      forceSixRows: true,
      startWithMonth,
      render: (data) => {
        Logger.debug('Rendering clndr instance');

        data.months = data.months.map((month) => this.mapMonth(month));

        this.calendar = data;

        return '';
      },
      weekOffset: 1,
    });
  }

  create(day: CalendarDay) {
    this.onCreateEvent.emit(day);
  }

  edit(day: CalendarDay) {
    this.onEditEvent.emit(day.events[0] as PortalEvent<any>);
  }

  mapDay(day: CalendarDay, index: number, days: CalendarDay[]): ExtendedDay {
    let extendedDay: ExtendedDay = {
      ...day,
      event: undefined,
    };

    if (extendedDay.events.length > 0) {
      const firstEvent = extendedDay.events[0];

      if (firstEvent instanceof SingleDayEvent) {
        // Inherited event
        if (firstEvent.inherited === true) {
          extendedDay.classes += ' inherited-event';

          if (firstEvent.excluded === true) {
            extendedDay.classes += ' excluded-event';
          }
        }
      } else {
        if (firstEvent.start !== undefined) {
          extendedDay.classes += ' multi-day-event';

          // Check if this is the first day of the multi-day-event
          if (days[index - 1]) {
            const prev = days[index - 1];
            const events = prev.events;

            if (events.length === 0 || events[0].title !== firstEvent.title) {
              extendedDay.classes += ' multi-day-event--start';
            }
          }

          // Check if this is the last day of the multi-day-event
          if (days[index + 1]) {
            const next = days[index + 1];
            const events = next.events;

            if (events.length === 0 || events[0].title !== firstEvent.title) {
              extendedDay.classes += ' multi-day-event--end';
            }
          }
        }

        for (const event of firstEvent.events) {
          if (DateUtils.equal(event.date, extendedDay.date)) {
            extendedDay.event = event;

            break;
          }
        }
      }
    }

    return extendedDay;
  }

  mapMonth(month: Month) {
    month.days = month.days.map((day, index) => this.mapDay(day, index, month.days));

    return month;
  }

  next() {
    this.instance.forward();
  }

  previous() {
    this.instance.back();
  }

  remove(day: CalendarDay) {
    this.onRemoveEvent.emit(day.events[0] as PortalEvent<any>);
  }
}
